import ReactGA from "react-ga4";
import mixpanel from "mixpanel-browser";

// Configuration
const GA_MEASUREMENT_ID = "G-R8GHRD2M1F";
const MIXPANEL_TOKEN = "79c9813c1e36e3624e89342b35681dab";

// Event name constants (snake_case)
export const ANALYTICS_EVENTS = {
  // Page views
  PAGE_VIEW: "page_view",
  PRODUCT_PAGE_VIEW: "product_page_view",
  DOWNLOAD_PAGE_VIEW: "download_page_view",
  
  // App store downloads
  PLAY_STORE_CLICK: "play_store_click",
  APP_STORE_CLICK: "app_store_click",
  
  // Download page specific
  DOWNLOAD_REDIRECT: "download_redirect",
  DOWNLOAD_MANUAL_CHOICE: "download_manual_choice",
  
  // General interactions
  BUTTON_CLICK: "button_click",
  OUTBOUND_LINK_CLICK: "outbound_link_click",
} as const;

// Platform detection results
export const PLATFORMS = {
  IOS: "ios",
  ANDROID: "android",
  UNKNOWN: "unknown",
} as const;

export type Platform = typeof PLATFORMS[keyof typeof PLATFORMS];

// Device info for analytics
export interface DeviceInfo {
  platform: Platform;
  os_name?: string;
  os_version?: string;
  device_type?: string;
  device_model?: string;
  device_vendor?: string;
  browser_name?: string;
  browser_version?: string;
}

// Location constants for badge tracking
export const BADGE_LOCATIONS = {
  HOME: "home",
  PRODUCT_DETAIL: "product_detail",
  PRODUCT_DETAIL_MOBILE: "product_detail_mobile",
  FOOTER: "footer",
  DOWNLOAD_PAGE: "download_page",
} as const;

export type BadgeLocation = typeof BADGE_LOCATIONS[keyof typeof BADGE_LOCATIONS];

let isGAInitialized = false;
let isMixpanelInitialized = false;

/**
 * Initialize Google Analytics 4
 */
export const initGA = () => {
  if (isGAInitialized) return;
  
  ReactGA.initialize(GA_MEASUREMENT_ID, {
    gaOptions: {
      anonymizeIp: true,
    },
  });
  isGAInitialized = true;
};

/**
 * Initialize Mixpanel
 */
export const initMixpanel = () => {
  if (isMixpanelInitialized) return;
  
  mixpanel.init(MIXPANEL_TOKEN, {
    autocapture: true,
    record_sessions_percent: 100,
  });
  isMixpanelInitialized = true;
};

/**
 * Initialize all analytics providers
 */
export const initAnalytics = () => {
  initGA();
  initMixpanel();
};

/**
 * Defer analytics initialization until after main content loads
 */
export const initAnalyticsDeferred = () => {
  if (typeof window === "undefined") return;
  
  const init = () => {
    try {
      initAnalytics();
      trackPageView();
    } catch (error) {
      console.warn("Failed to initialize analytics:", error);
    }
  };
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === "function") {
    ric(init, { timeout: 3000 });
  } else {
    setTimeout(init, 1500);
  }
};

/**
 * Check if analytics is ready
 */
export const isAnalyticsReady = () => isGAInitialized && isMixpanelInitialized;

/**
 * Track page views (GA + Mixpanel)
 */
export const trackPageView = (path?: string) => {
  if (globalThis.window === undefined) return;
  
  const pagePath = path || globalThis.location.pathname + globalThis.location.search;
  
  // GA4
  if (isGAInitialized) {
    ReactGA.send({
      hitType: "pageview",
      page: pagePath,
    });
  }
  
  // Mixpanel
  if (isMixpanelInitialized) {
    mixpanel.track(ANALYTICS_EVENTS.PAGE_VIEW, {
      path: pagePath,
    });
  }
};

/**
 * Track product page views with product details
 */
export const trackProductPageView = (productId: string, productName: string) => {
  if (typeof globalThis.window === "undefined") return;
  
  const pagePath = globalThis.location.pathname;
  
  // GA4
  if (isGAInitialized) {
    ReactGA.event({
      category: "product",
      action: ANALYTICS_EVENTS.PRODUCT_PAGE_VIEW,
      label: productId,
    });
  }
  
  // Mixpanel
  if (isMixpanelInitialized) {
    mixpanel.track(ANALYTICS_EVENTS.PRODUCT_PAGE_VIEW, {
      product_id: productId,
      product_name: productName,
      path: pagePath,
    });
  }
};

/**
 * Track Play Store badge click
 */
export const trackPlayStoreClick = (location: BadgeLocation, productId?: string) => {
  // GA4
  if (isGAInitialized) {
    ReactGA.event({
      category: "app_download",
      action: ANALYTICS_EVENTS.PLAY_STORE_CLICK,
      label: location,
    });
  }
  
  // Mixpanel
  if (isMixpanelInitialized) {
    mixpanel.track(ANALYTICS_EVENTS.PLAY_STORE_CLICK, {
      platform: "android",
      location,
      product_id: productId || "zero",
    });
  }
};

/**
 * Track App Store badge click
 */
export const trackAppStoreClick = (location: BadgeLocation, productId?: string) => {
  // GA4
  if (isGAInitialized) {
    ReactGA.event({
      category: "app_download",
      action: ANALYTICS_EVENTS.APP_STORE_CLICK,
      label: location,
    });
  }
  
  // Mixpanel
  if (isMixpanelInitialized) {
    mixpanel.track(ANALYTICS_EVENTS.APP_STORE_CLICK, {
      platform: "ios",
      location,
      product_id: productId || "zero",
    });
  }
};

/**
 * Track custom events (sends to both GA and Mixpanel)
 */
export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>
) => {
  // GA4
  if (isGAInitialized) {
    ReactGA.event({
      category: properties?.category as string || "general",
      action: eventName,
      label: properties?.label as string,
      value: properties?.value as number,
    });
  }
  
  // Mixpanel
  if (isMixpanelInitialized) {
    mixpanel.track(eventName, properties);
  }
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName: string, location?: string) => {
  trackEvent(ANALYTICS_EVENTS.BUTTON_CLICK, {
    button_name: buttonName,
    location,
  });
};

/**
 * Track outbound links
 */
export const trackOutboundLink = (url: string) => {
  trackEvent(ANALYTICS_EVENTS.OUTBOUND_LINK_CLICK, {
    url,
  });
};

/**
 * Track download page view with detected device info
 */
export const trackDownloadPageView = (deviceInfo: DeviceInfo) => {
  // GA4
  if (isGAInitialized) {
    ReactGA.event({
      category: "download",
      action: ANALYTICS_EVENTS.DOWNLOAD_PAGE_VIEW,
      label: deviceInfo.platform,
    });
  }
  
  // Mixpanel - send rich device data
  if (isMixpanelInitialized) {
    mixpanel.track(ANALYTICS_EVENTS.DOWNLOAD_PAGE_VIEW, {
      detected_platform: deviceInfo.platform,
      os_name: deviceInfo.os_name,
      os_version: deviceInfo.os_version,
      device_type: deviceInfo.device_type,
      device_model: deviceInfo.device_model,
      device_vendor: deviceInfo.device_vendor,
      browser_name: deviceInfo.browser_name,
      browser_version: deviceInfo.browser_version,
    });
  }
};

/**
 * Track automatic redirect from download page
 */
export const trackDownloadRedirect = (platform: "ios" | "android") => {
  // GA4
  if (isGAInitialized) {
    ReactGA.event({
      category: "download",
      action: ANALYTICS_EVENTS.DOWNLOAD_REDIRECT,
      label: platform,
    });
  }
  
  // Mixpanel
  if (isMixpanelInitialized) {
    mixpanel.track(ANALYTICS_EVENTS.DOWNLOAD_REDIRECT, {
      platform,
      redirect_type: "automatic",
    });
  }
};

/**
 * Track manual store choice from download page (when platform unknown)
 */
export const trackDownloadManualChoice = (chosenPlatform: "ios" | "android") => {
  // GA4
  if (isGAInitialized) {
    ReactGA.event({
      category: "download",
      action: ANALYTICS_EVENTS.DOWNLOAD_MANUAL_CHOICE,
      label: chosenPlatform,
    });
  }
  
  // Mixpanel
  if (isMixpanelInitialized) {
    mixpanel.track(ANALYTICS_EVENTS.DOWNLOAD_MANUAL_CHOICE, {
      chosen_platform: chosenPlatform,
      redirect_type: "manual",
    });
  }
};

// Legacy exports for backwards compatibility
export const initGADeferred = initAnalyticsDeferred;
export const isGAReady = () => isGAInitialized;
